import React from 'react';
import styles from './dam-info.module.scss';
import { connect } from 'react-redux';
import * as selectors from '../../reducers';
import PropTypes from 'prop-types';
import { setActivePage } from '../../actions/ui';
import { BarChart } from '../bar-chart';
import { humanizeNumber, numberWithCommas } from '../../helpers/formatters';
import DefinitionMapper from '../../helpers/definition-mapper';
import { LabelMapper } from '../../helpers/label-mapper';

function median(numbers) {
  const count = numbers.length;
  numbers.sort((a, b) => a - b);
  return count % 2 === 0
    ? (numbers[count / 2 - 1] + numbers[count / 2]) / 2
    : numbers[(count - 1) / 2];
}

const DamInfoComponent = (props) => {
  const chartStats = [
    { key: 'currentVolume', label: 'Current volume', unit: 'ml' },
    { key: 'capacity', label: 'Capacity', unit: 'ml' },
    { key: 'fullSupplyLevel', label: 'Full Supply Level', unit: 'm' },
    { key: 'catchmentArea', label: 'Catchment Area', unit: 'ha' },
    { key: 'yearsOld', label: 'Age', unit: 'years' },
  ];
  const tableStats = [
    {
      key: 'upgradeInformation',
      fields: [
        'dateOfLastUpgrade',
        'upgradeTargetDate',
        'acceptableFloodCapacity',
      ],
    },
    {
      key: 'damSafety',
      fields: [
        'populationAtRisk',
        'consequenceCategory',
        'failureImpactRating',
        'emergencyActionPlan',
        'localDisasterManagementGroup',
        'districtDisasterManagementGroup',
        'dateOfLastAudit',
      ],
    },
    {
      key: 'storage',
      fields: [
        'capacity',
        'fullSupplyLevel',
        'reducedFullSupply',
        'currentVolume',
      ],
    },
    {
      key: 'construction',
      fields: [
        'damType',
        'material',
        'embankmentMaxHeight',
        'spillwayType',
        'spillwayNoOfGates',
      ],
    },
  ];
  return (
    <div>
      <div style={{ fontSize: 24, margin: '40px 0 30px', color: '#28336e' }}>
        Key statistics
      </div>
      {chartStats.map((x) => (
        <StatisticChartComponent key={x.key} stat={x} {...props} />
      ))}
      {tableStats.map((x) => (
        <StatisticTableComponent key={x.key} stat={x} {...props} />
      ))}
    </div>
  );
};

const StatisticChartComponent = ({ stat, selectedDam, dams, allDams }) => {
  const medianVal = median(
    dams.filter((x) => typeof x[stat.key] === 'number').map((x) => x[stat.key])
  );

  let note = '';

  if (selectedDam.id === 2002) {
    switch (stat.key) {
      case 'capacity':
        note =
          'The dam has an upper and lower reservoir. Capacity: Upper is 37ML, Lower is 5ML.';
        break;
      case 'currentVolume':
        note =
          'The dam has an upper and lower reservoir. There is currently no data available for current volume.';
        break;
      case 'fullSupplyLevel':
        note =
          'The dam has an upper and lower reservoir. Full Supply Level: Upper is 22m, Lower is 26m.';
        break;
      case 'catchmentArea':
        note =
          'The dam has an upper and lower reservoir. Catchment Area: Upper is 39ha, Lower is 51ha.';
        break;

      default:
        note = '';
        break;
    }
  } else {
    if (selectedDam[stat.key] === '-') {
      note = 'There is currently no data available for this dam';
    }
  }

  return (
    <div className={styles.chartComponent}>
      <div className={styles.title}>
        {LabelMapper[stat.key].replace('(ML)', '').replace('(m)', '')}
      </div>
      <div className={styles.chart}>
        <BarChart
          key={`${selectedDam.id}-${stat.key}-bar`}
          data={{
            min: 0,
            max: Math.max(
              ...dams.map((x) =>
                typeof x[stat.key] === 'number' ? x[stat.key] : 0
              )
            ),
            value:
              typeof selectedDam[stat.key] === 'number'
                ? selectedDam[stat.key]
                : '-',
          }}
          color={'purple'}
          unit={stat.unit}
          visible={true}
          isStatic={true}
        >
          {selectedDam.dam}
        </BarChart>
        <small style={{ color: '#87878A', fontSize: 14, fontStyle: 'italic' }}>
          {note}
          <br />
          <br />
        </small>
        {dams.length > 1 && (
          <div>
            <BarChart
              key={`${selectedDam.id}-${stat.key}-med-bar`}
              data={{
                min: 0,
                max: Math.max(
                  ...dams.map((x) =>
                    typeof x[stat.key] === 'number' ? x[stat.key] : 0
                  )
                ),
                value: Math.round(medianVal * 10) / 10 || '-',
              }}
              color={'purple'}
              unit={stat.unit}
              visible={true}
              isStatic={true}
            >
              Median {LabelMapper[stat.key]} of {dams.length} dams
            </BarChart>
            <br />
            <small className={styles.smallDescription}>
              {stat.key === 'fullSupplyLevel' &&
                'Full supply level measures the height of water in meters against the dam wall. '}
              {LabelMapper[stat.key].charAt(0).toUpperCase() +
                LabelMapper[stat.key].slice(1)}{' '}
              reported {stat.key === 'currentVolume' && '(as at November 2021)'}{' '}
              for Queensland water storage facilities in the region ranged from{' '}
              {humanizeNumber(
                Math.min(
                  ...dams.map((x) =>
                    typeof x[stat.key] === 'number' ? x[stat.key] : 0
                  )
                ),
                stat.unit
              )}{' '}
              to{' '}
              {humanizeNumber(
                Math.max(
                  ...dams.map((x) =>
                    typeof x[stat.key] === 'number' ? x[stat.key] : 0
                  )
                ),
                stat.unit
              )}
              . Median is the middle value in the selected data range.
            </small>
          </div>
        )}
      </div>
    </div>
  );
};

const StatisticTableComponent = ({ stat, selectedDam }) => {
  return (
    <div className={styles.tableComponent}>
      <div className={styles.title}>{LabelMapper[stat.key]}</div>
      <div style={{ fontSize: 16, color: '#4c4c4c' }}>
        {DefinitionMapper(stat.key)}
      </div>
      <br />
      <div className={styles.table}>
        <table
          style={{
            width: '100%',
            border: '1px solid #E3E4E6',
            borderCollapse: 'collapse',
          }}
        >
          <tbody>
            {stat.fields.map((x) => (
              <tr key={x}>
                <td className={styles.tableHeading}>{LabelMapper[x]}</td>
                <td style={{ width: '50%', padding: 10, color: '#333333' }}>
                  {x === 'emergencyActionPlan' ? (
                    <React.Fragment>
                      {selectedDam[x] !== '' ? (
                        <React.Fragment>
                          <a
                            href={selectedDam[x]}
                            target="_blank"
                          >{`${selectedDam.dam} EAP.pdf`}</a>
                          <small>
                            The plan is also available from the{' '}
                            <a
                              href="https://www.business.qld.gov.au/industries/mining-energy-water/water/industry-infrastructure/dams/emergency-action-plans/map"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Map and list of emergency action plans for
                              referable dams
                            </a>
                            .
                          </small>
                        </React.Fragment>
                      ) : (
                        <span>Currently under review</span>
                      )}
                    </React.Fragment>
                  ) : x === 'upgradeTargetDate' ||
                    x === 'dateOfLastUpgrade' ||
                    x === 'dateOfLastAudit' ? (
                    selectedDam[x] || 'Data unavailable'
                  ) : (
                    numberWithCommas(selectedDam[x]) || 'Data unavailable'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  setActivePage,
};

const mapStateToProps = (state, props) => ({
  isMobile: selectors.getIsMobile(state),
  allDams: selectors.getDams(state),
});

DamInfoComponent.propTypes = {
  region: PropTypes.object,
  dams: PropTypes.arrayOf(PropTypes.object),
  allDams: PropTypes.arrayOf(PropTypes.object),
  setActivePage: PropTypes.func,
};

const DamInfo = connect(mapStateToProps, mapDispatchToProps)(DamInfoComponent);
export { DamInfo };



// WEBPACK FOOTER //
// ./src/components/dam-info/dam-info.js